import { retryWithAuth } from "./utils/retryWithAuth";

import { ListParams } from "../models/listParams";
import { Beoordeling } from "../models/plan/beoordeling";
import { LayerConfiguration } from "../models/maps/layerConfiguration";

import { PlanManagementWebService } from "../webservices/plan-management.web.service";
import { BaseService } from "./base.service";
import { Layer } from "../models/maps/layer";
import { LayerDetails } from "../models/maps/layerDetails";
import { GeoPackageType } from "../models/types/geoPackageType";
import { LayerType } from "../models/types/layerType";


export class PlanManagementService extends BaseService {
  private static instance: PlanManagementService;

  // // Private constructor to prevent direct instantiation
  // private constructor() {
  //   super();
  // }

  // Method to return the singleton instance
  public static getInstance(): PlanManagementService {
    if (!PlanManagementService.instance) {
      PlanManagementService.instance = new PlanManagementService();
    }
    return PlanManagementService.instance;
  }

  // Azure maps

  public getAzureMapsToken(): Promise<{ token: string; expiresAtUtc: string }> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planManagementWebService.getAzureMapsToken(organisatie.id), 2)
    );
  }

  // GeoPackage 

  public uploadGeoPackage(type: GeoPackageType, file: File): Promise<any> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planManagementWebService.uploadGeoPackage(type, organisatie.id, file), 2)
    );
  }

  public getGeoPackageStatus(type: GeoPackageType, ): Promise<any> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planManagementWebService.getGeoPackageStatus(type, organisatie.id), 2)
    );
  }

  public getGeoPackageData(type: GeoPackageType, timestamp: string): Promise<any> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planManagementWebService.getGeoPackageData(type, organisatie.id, timestamp), 2)
    );
  }

  public processGeoPackage(type: GeoPackageType, timestamp: string): Promise<any> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planManagementWebService.processGeoPackage(type, organisatie.id, timestamp), 2)
    );
  }

  public deleteGeoPackage(type: GeoPackageType, timestamp: string): Promise<any> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planManagementWebService.deleteGeoPackage(type, organisatie.id, timestamp), 2)
    );
  }

  // Layer configuation on type

  public getLayerConfigurations(type: LayerType, params: ListParams): Promise<any> {
    switch (type) {
      case LayerType.ASSET:
        return this.getAssetLayerConfigurations(params);
      case LayerType.PUBLIC:
      default:
        return this.getPublicLayerConfigurations(params);
    }
  }

  public getLayerConfiguration(type: LayerType, layerId: string): Promise<LayerConfiguration> {
    switch (type) {
      case LayerType.ASSET:
        return this.getAssetLayerConfiguration(layerId);
      case LayerType.PUBLIC:
      default:
        return this.getPublicLayerConfiguration(layerId);
    }
  }

  public enableLayerConfiguration(type: LayerType, layerId: string): Promise<LayerConfiguration> {
    switch (type) {
      case LayerType.ASSET:
        return this.enableAssetLayerConfiguration(layerId);
      case LayerType.PUBLIC:
      default:
        return this.enablePublicLayerConfiguration(layerId);
    }
  }

  public disableLayerConfiguration(type: LayerType, layerId: string): Promise<LayerConfiguration> {
    switch (type) {
      case LayerType.ASSET:
        return this.disableAssetLayerConfiguration(layerId);
      case LayerType.PUBLIC:
      default:
        return this.disablePublicLayerConfiguration(layerId);
    }
  }

  // Asset Layers Configurations

  // Get all asset layer configurations for an organization.
  private getAssetLayerConfigurations(params: ListParams): Promise<any> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planManagementWebService.getAssetLayerConfigurations(organisatie.id, params), 2)
    );
  }

  // Get a specific asset layer configuration for an organization.
  private getAssetLayerConfiguration(layerId: string): Promise<LayerConfiguration> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie => 
      retryWithAuth(() => planManagementWebService.getAssetLayerConfiguration(layerId, organisatie.id), 2)
    );
  }

  // Enable asset layer usage for an organization.
  private enableAssetLayerConfiguration(layerId: string): Promise<LayerConfiguration> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planManagementWebService.putAssetLayerConfiguration(layerId, organisatie.id), 2)
    );
  }

  // Disable asset layer usage for an organization.
  private disableAssetLayerConfiguration(layerId: string): Promise<LayerConfiguration> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planManagementWebService.deleteAssetLayerConfiguration(layerId, organisatie.id), 2)
    );
  }

  // Public Layers Configurations

  // Get all layer configurations for an organization.
  private getPublicLayerConfigurations(params: ListParams): Promise<any> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planManagementWebService.getLayerConfigurations(organisatie.id, params), 2)
    );
  }

  // Get a specific layer configuration for an organization.
  private getPublicLayerConfiguration(layerId: string): Promise<LayerConfiguration> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie => 
      retryWithAuth(() => planManagementWebService.getLayerConfiguration(layerId, organisatie.id), 2)
    );
  }

  // Enable layer usage for an organization.
  private enablePublicLayerConfiguration(layerId: string): Promise<LayerConfiguration> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planManagementWebService.putLayerConfiguration(layerId, organisatie.id), 2)
    );
  }

  // Disable layer usage for an organization.
  private disablePublicLayerConfiguration(layerId: string): Promise<LayerConfiguration> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planManagementWebService.deleteLayerConfiguration(layerId, organisatie.id), 2)
    );
  }

  // Layers

  public async getActiveLayers(): Promise<Layer[]> {
    const params: ListParams = {
      _offset: 0,
      _limit: 100,
    };

    let data = await this.getLayers(params);
    if (data && data.count) {
      const layers: Layer[] = data.items;
      return layers;
    }
    
    return [];
  }

  // Get layers for an organization.
  public getLayers(params: ListParams): Promise<any> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planManagementWebService.getLayers(organisatie.id, params), 2)
    );
  }

  // Get asset layers for an organization.
  public getAssetLayers(params: ListParams): Promise<any> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(
        () => planManagementWebService.getAssetLayers(organisatie.id, params),
        2,
        { redirectOnForbidden: false }
      )
    ).catch((error: any) => {
      if (this.isForbiddenError(error)) {
        console.warn('[PlanManagementService] 403 ontvangen voor de huidige rol bij GET /api/v1/asset-layers; fallback naar lege lijst.');
        return {
          count: 0,
          offset: params?._offset ?? 0,
          limit: params?._limit ?? 0,
          items: [],
        };
      }

      throw error;
    });
  }

  public getAssetLayer(assetLayerId: string): Promise<any> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planManagementWebService.getAssetLayer(assetLayerId, organisatie.id), 2)
    );
  }

  public updateAssetLayerDetails(layerId: string, details: LayerDetails): Promise<any> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planManagementWebService.putAssetLayerDetails(layerId, details, organisatie.id), 2)
    );
  }

  public deleteAssetLayer(layerId: string): Promise<any> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planManagementWebService.deleteAssetLayer(layerId, organisatie.id), 2)
    );
  }

  public addShareAssetLayerWithOrganisation(assetLayerId: string, targetOrganizationId: string): Promise<any> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planManagementWebService.putAssetLayerShareTarget(assetLayerId, targetOrganizationId, organisatie.id), 2)
    );
  }

  public deleteShareAssetLayerWithOrganisation(assetLayerId: string, targetOrganizationId: string): Promise<any> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planManagementWebService.deleteAssetLayerShareTarget(assetLayerId, targetOrganizationId, organisatie.id), 2)
    );
  }

  public enableAssetLayerUsePlanShareSettings(assetLayerId: string): Promise<any> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planManagementWebService.putAssetLayerShareAsPlan(assetLayerId, organisatie.id), 2)
    );
  }

  public disableAssetLayerUsePlanShareSettings(assetLayerId: string): Promise<any> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planManagementWebService.deleteAssetLayerShareAsPlan(assetLayerId, organisatie.id), 2)
    );
  }

  // Get a specific layer for an organization.
  public getLayer(layerId: string): Promise<LayerConfiguration> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie => 
      retryWithAuth(() => planManagementWebService.getLayer(layerId, organisatie.id), 2)
    );
  }

  // Plan Beoordeling

  public getPlanBeoordelingenCurrent(infplId: string): Promise<any> {
    return this.getPlanBeoordelingen(infplId, 'current');
  }

  public getPlanBeoordelingenHistory(infplId: string, orgId: string): Promise<any> {
    return this.getPlanBeoordelingen(infplId, 'history', orgId);
  }

  public getPlanBeoordelingen(infplId: string, type: string, orgId: string | null = null): Promise<any> {
    const planManagementWebService = new PlanManagementWebService();

    // TODO: refactor with new OGC
    const params = {
      state: type, // history
      offset: 0,
      limit: 100,
      id: orgId ? orgId : null,
      // id: '',
    };
    
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planManagementWebService.getPlanBeoordelingen(infplId, organisatie.id, params), 2)
    );
  }

  public addPlanBeoordeling(beoordeling: Beoordeling, infplId: string): Promise<any> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planManagementWebService.putPlanBeoordeling(beoordeling, infplId, organisatie.id), 2)
    );
  }

  // Sharing

  public getOrganisatiesToShare(params: ListParams, isSharePerPlan: boolean): Promise<any> {
    return isSharePerPlan
      ? this.getOrganisatiesToSharePlans(params)
      : this.getOrganisatiesToShareGebied(params);
  }

  // Sharing gebied (alle plannen)

  private getOrganisatiesToShareGebied(params: ListParams): Promise<any> {
    const planManagementWebService = new PlanManagementWebService();
    return retryWithAuth(() => planManagementWebService.getOrganisationsToShare(params), 2);
  }

  public getSharedOrganisaties(params: ListParams): Promise<any> {
    const planManagementWebService = new PlanManagementWebService();
    
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planManagementWebService.getSharedOrganisations(organisatie.id, params), 2)
    );
  }

  public addSharedOrganisatie(sharedId: string): Promise<any> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planManagementWebService.putSharedOrganisation(sharedId, organisatie.id), 2)
    );
  }

  public deleteSharedOrganisatie(sharedId: string): Promise<any> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planManagementWebService.deleteSharedOrganisation(sharedId, organisatie.id), 2)
    );
  }

  // Plan Sharing

  private getOrganisatiesToSharePlans(params: ListParams): Promise<any> {
    const planManagementWebService = new PlanManagementWebService();
    return retryWithAuth(() => planManagementWebService.getOrganisatiesToSharePlans(params), 2);
  }

  public getOrganisationsSharedWithPlan(infplId: string, params: ListParams): Promise<any> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planManagementWebService.getOrganisationsSharedWithPlan(infplId, organisatie.id, params), 2)
    );
  }

  public addSharePlanWithOrganisation(infplId: string, sharedId: string): Promise<any> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planManagementWebService.putSharePlanWithOrganisation(infplId, sharedId, organisatie.id), 2)
    );
  }

  public deleteSharePlanWithOrganisation(infplId: string, sharedId: string): Promise<any> {
    const planManagementWebService = new PlanManagementWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planManagementWebService.deleteSharePlanWithOrganisation(infplId, sharedId, organisatie.id), 2)
    );
  }
};

// Export an instance of the PlanManagementService class
export const planManagementService = PlanManagementService.getInstance();